import type { ComponentPropsWithoutRef } from 'react';
import { useContext } from 'react';
import { Tooltip, TooltipTitleWithShortcut } from '@frameio/vapor';
import type { CommandIds } from '../types/Command';
import type { CommandKeys } from '../types/CommandKeys';
import { CommandRegistryContext } from './CommandRegistryContext';

type Props = {
  /** Command ID, one of the options in `COMMAND_LIST.ts` */
  id: CommandIds;
} & ComponentPropsWithoutRef<typeof Tooltip>;

/**
 * Renders a tooltip with the given command's label and keyboard shortcut.
 *
 * @example
 * ```
 * const usage = (
 *   <KeyboardShortcutTooltip id="my.id">
 *     <IconButton icon={<Icon />} onClick={onClick} />
 *   </KeyboardShortcutTooltip>
 * );
 * ```
 */
export function KeyboardShortcutTooltip({ children, id, ...rest }: Props) {
  const registry = useContext(CommandRegistryContext);
  const { keys, displayLabel } = registry?.get(id) || {};

  const effectiveKeys = keys as Array<CommandKeys> | undefined;

  const title = effectiveKeys ? (
    <TooltipTitleWithShortcut<CommandKeys> keys={effectiveKeys}>
      {displayLabel}
    </TooltipTitleWithShortcut>
  ) : (
    displayLabel
  );

  return (
    <Tooltip {...rest} title={title}>
      {children}
    </Tooltip>
  );
}
